import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Banknote } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PLAN_COLORS = { starter: '#10b981', professional: '#3b82f6', enterprise: '#8b5cf6' };

export default function AdminPayments() {
  const { adminToken } = useAdminAuth();
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState('');

  const fetchPayments = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/payments`, {
        params: { page, limit: 20, plan: planFilter },
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPayments(res.data.payments);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [adminToken, planFilter]);

  useEffect(() => { fetchPayments(1); }, [fetchPayments]);

  const totalRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <AdminLayout>
      <div>
        <h1 style={styles.pageTitle}>Payments</h1>
        <p style={styles.pageSub}>Total records: <strong>{pagination.total}</strong></p>

        {/* Revenue Summary */}
        <div style={styles.revCard}>
          <div>
            <div style={styles.revLabel}>Estimated Revenue (Current Page)</div>
            <div style={styles.revAmount}>₹{totalRevenue.toLocaleString('en-IN')}</div>
          </div>
          <div style={styles.revIcon}><Banknote size={40} color="#f59e0b" /></div>
        </div>

        {/* Filter */}
        <div style={styles.filters}>
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={styles.filterSelect}>
            <option value="" style={{background: '#1e293b', color: '#fff'}}>All Plans</option>
            <option value="starter" style={{background: '#1e293b', color: '#fff'}}>Starter</option>
            <option value="professional" style={{background: '#1e293b', color: '#fff'}}>Professional</option>
            <option value="enterprise" style={{background: '#1e293b', color: '#fff'}}>Enterprise</option>
          </select>
        </div>

        {/* Table */}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                {['User', 'Plan', 'Amount', 'Razorpay ID', 'Expiry', 'Date'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={styles.emptyTd}>⏳ Loading...</td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan={6} style={styles.emptyTd}>No payment records found.</td></tr>
              ) : payments.map((p, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.userCell}>
                      <div style={styles.avatar}>{p.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div style={styles.userName}>{p.name}</div>
                        <div style={styles.userEmail}>{p.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.planBadge, background: `${PLAN_COLORS[p.plan]}20`, color: PLAN_COLORS[p.plan] || '#fff', border: `1px solid ${PLAN_COLORS[p.plan]}40` }}>
                      {p.plan}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.amount}>₹{p.amount}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.razorpayId}>{p.razorpayId || '—'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.dateText}>{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-IN') : '—'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.dateText}>{new Date(p.paidAt).toLocaleDateString('en-IN')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={styles.pagination}>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => fetchPayments(p)}
                style={{ ...styles.pageBtn, background: p === pagination.page ? '#f59e0b' : 'rgba(255,255,255,0.06)', color: p === pagination.page ? '#000' : '#fff' }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const styles = {
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 6px' },
  pageSub: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', margin: '0 0 20px' },
  revCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(239,68,68,0.08))',
    border: '1px solid rgba(245,158,11,0.2)',
    borderRadius: '14px',
    padding: '24px 28px',
    marginBottom: '20px',
  },
  revLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' },
  revAmount: { fontSize: '32px', fontWeight: '800', color: '#f59e0b' },
  revIcon: { fontSize: '40px' },
  filters: { display: 'flex', gap: '12px', marginBottom: '20px' },
  filterSelect: {
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  tableWrapper: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'rgba(255,255,255,0.05)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '14px 16px', verticalAlign: 'middle' },
  emptyTd: { padding: '48px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '15px' },
  userCell: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: '700', fontSize: '14px', flexShrink: 0,
  },
  userName: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  userEmail: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  planBadge: { fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '100px', textTransform: 'capitalize' },
  amount: { fontSize: '15px', fontWeight: '700', color: '#10b981' },
  razorpayId: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' },
  dateText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  pagination: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' },
  pageBtn: { width: 36, height: 36, borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px' },
};
